/**
 * Erasing your own account (ADR 0010 Decision 7, ADR 0012 step 28): what the portal says before it
 * happens, and what a failure means. Imports nothing, so `erasure.test.js` runs it under `node --test`.
 */

export interface ErasureCard {
  roomName: string;
  cardId: string;
}

/**
 * The three things ADR 0010 Decision 7 says a player is told before confirming: what goes, what each
 * room keeps, and that the sign-in itself is removed separately, by an administrator within 30 days. All three, every time — a confirmation
 * missing the second reads as a promise that the room forgets the results too, and one missing the
 * third as a promise that the sign-in stops working.
 */
export interface ErasureDisclosure {
  deleted: string[];
  kept: string[];
  signIn: string;
}

/**
 * What happens to the sign-in, said the same way before and after. A timeframe the platform commits to —
 * an administrator's queue in admin-web holds each removal and flags it before the 30 days run out — and
 * what signing in reaches meanwhile. "An account holding nothing" read as an account still existing.
 */
const SIGN_IN_REMOVED =
  'Your sign-in is removed by a platform administrator within 30 days. Until then you can still sign in, but Brush will hold nothing about you — no cards, no history.';

export function erasureDisclosure(cards: readonly ErasureCard[]): ErasureDisclosure {
  const connections =
    cards.length === 0
      ? 'Any connection between this sign-in and a player card.'
      : `The connection between this sign-in and your ${cards.length === 1 ? 'card' : `${cards.length} cards`}.`;

  return {
    deleted: [
      'Your Brush account.',
      connections,
      'The record of how you have used the portal. Anything that must stay on the record — that you connected or disconnected a card — is kept without your name or account on it.',
    ],
    kept:
      cards.length === 0
        ? ['You have no card connected, so no room holds anything of yours through this account.']
        : cards.map(
            ({ roomName, cardId }) =>
              `${roomName} keeps card ${cardId} and every result, standing, prize and fantasy entry recorded against it — those are the room's records.`,
          ),
    signIn: SIGN_IN_REMOVED,
  };
}

/**
 * What a failed erasure means, and therefore where it sends the person.
 *
 * Read structurally — `status` and `code` from an `ApiError`, `errorCode` from MSAL's `BrowserAuthError`
 * — so the rule runs without either library. A popup closed by the person is a choice and a blocked one
 * is the browser's, and they are worded apart: the first needs nothing, the second needs the browser
 * told to allow it.
 *
 * **Under MSAL v5 a closed window arrives as `timed_out`, not `user_cancelled`.** Entra serves its
 * sign-in pages with Cross-Origin-Opener-Policy, which cuts the opener off from the popup, so MSAL no
 * longer watches for it closing: the opener waits on a BroadcastChannel and gives up after
 * `FRESH_SIGN_IN_TIMEOUT_MS` (@brush/auth). `timed_out` therefore means *the window was closed, or the
 * sign-in in it was not finished in time* — the two are indistinguishable from here — and never a
 * network failure, which is what it read as while it fell through to `other`.
 *
 * **`cancelled` is kept, though effectively unreachable.** In msal-browser 5.20.0 `user_cancelled` is
 * raised only by the popup-relay mechanism (`popupRelayUri`, unused here) and by a native broker's
 * prompt being dismissed (`InteractionHandler.handleCodeResponse`) — neither of which this portal does.
 * It costs one branch, and it is the right answer the day either arrives or MSAL learns to see a closed
 * window again.
 */
export type ErasureFailure =
  | { kind: 'sign_in_again' }
  | { kind: 'nothing_to_erase' }
  | { kind: 'cancelled' }
  | { kind: 'sign_in_timed_out' }
  | { kind: 'popup_blocked' }
  | { kind: 'other' };

export function erasureFailure(error: unknown): ErasureFailure {
  if (typeof error !== 'object' || error === null) return { kind: 'other' };
  const { status, code, errorCode } = error as { status?: unknown; code?: unknown; errorCode?: unknown };

  if (status === 403 && code === 'sign_in_again_required') return { kind: 'sign_in_again' };
  if (status === 404 && code === 'nothing_to_erase') return { kind: 'nothing_to_erase' };
  if (errorCode === 'user_cancelled') return { kind: 'cancelled' };
  if (errorCode === 'timed_out') return { kind: 'sign_in_timed_out' };
  if (errorCode === 'popup_window_error' || errorCode === 'empty_window_error') return { kind: 'popup_blocked' };
  return { kind: 'other' };
}

/**
 * The sentence for a failure. The erasure is one transaction, so a refusal the server gave — or a sign-in
 * that never happened — deleted nothing, and saying so is what stops somebody wondering whether half their
 * account is gone. `other` takes the API's own sentence and claims nothing: a response lost after the
 * commit is a failure the browser sees over an erasure that happened.
 */
export function erasureFailureMessage(failure: ErasureFailure, fallback: string | null): string | null {
  switch (failure.kind) {
    case 'sign_in_again':
      return 'Your sign-in was more than five minutes old by the time it reached Brush, so nothing was deleted. Try again and confirm straight after signing in.';
    case 'cancelled':
      return 'You closed the sign-in window, so nothing was deleted.';
    case 'sign_in_timed_out':
      return 'The sign-in window was closed, or the sign-in was not finished in time, so nothing was deleted. Try again when you are ready to type the code.';
    case 'popup_blocked':
      return 'Your browser blocked the sign-in window, so nothing was deleted. Allow pop-ups for this site and try again.';
    case 'nothing_to_erase':
      return 'Brush holds nothing for this sign-in — there is no account to delete.';
    case 'other':
      return fallback;
  }
}

/**
 * Where an erasure is, for the button that started it: at rest, waiting for the fresh sign-in, or
 * sending the deletion.
 *
 * **Two pending phases rather than one, because the first can be long and can end in nothing.** The
 * fresh sign-in is a popup (`freshSignIn` in @brush/auth), and its wait is five minutes
 * (`FRESH_SIGN_IN_TIMEOUT_MS`, MSAL's `popupBridgeTimeout`) — the clock starts when the window opens,
 * so it has to cover fetching an emailed code. And Entra's Cross-Origin-Opener-Policy leaves the portal
 * unable to see the window close, so somebody who closes it is still in this phase until the wait runs
 * out, with nothing ever deleted. "Deleting…" across all of that was untrue for up to five minutes on
 * an action that cannot be undone. It is said only once the sign-in has come back and the DELETE is on
 * its way.
 *
 * `signedIn` is whether *this* attempt's sign-in has come back. Nothing pending is at rest whatever an
 * earlier attempt got as far as, so a failed deletion never leaves the button saying it is deleting.
 */
export type ErasurePhase = 'idle' | 'signing_in' | 'deleting';

export function erasurePhase(pending: boolean, signedIn: boolean): ErasurePhase {
  if (!pending) return 'idle';
  return signedIn ? 'deleting' : 'signing_in';
}

/** The erase button's label in each phase. */
export function erasureButtonLabel(phase: ErasurePhase): string {
  switch (phase) {
    case 'idle':
      return 'Sign in again and delete';
    case 'signing_in':
      return 'Waiting for you to sign in…';
    case 'deleting':
      return 'Deleting…';
  }
}

/** Where the portal remembers, across the sign-out that follows an erasure, that it happened. */
export const ERASED_KEY = 'brush.player.accountErased';

export interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** What the landing page says once, after the sign-out that follows an erasure. */
export const ERASED_NOTICE = `Your Brush account has been deleted. ${SIGN_IN_REMOVED}`;

/**
 * Remembers that an erasure happened, for the landing page the sign-out returns to. Session storage
 * survives that round trip in the same tab and nothing else. A storage that refuses is a landing page
 * without the notice, never a failed erasure — the account is already gone by now.
 */
export function markErased(storage: NoticeStorage): void {
  try {
    storage.setItem(ERASED_KEY, 'true');
  } catch {
    // A private window or blocked site data: the notice is lost, the erasure is not.
  }
}

/** The notice, once: taking it removes it, so a reload of the landing page does not repeat it. */
export function takeErasedNotice(storage: NoticeStorage): string | null {
  try {
    if (storage.getItem(ERASED_KEY) !== 'true') return null;
    storage.removeItem(ERASED_KEY);
    return ERASED_NOTICE;
  } catch {
    return null;
  }
}
